import { useEffect, useRef, useState } from "react";
import { updateDriverLocation } from "../../services/driver";

const SEND_INTERVAL_MS = 15000;

export interface DriverPosition {
  lat: number;
  lng: number;
}

export type LocationSharingError = "denied" | "unavailable" | "unsupported" | "send";

/**
 * Kurye çevrimiçiyken GPS konumunu izler ve en fazla 15 sn'de bir sunucuya yollar.
 * Cihaz hareketsizken watchPosition tetiklenmeyebildiği için son konum periyodik olarak
 * tekrar gönderilir; böylece müşteri tarafı kuryenin hâlâ aktif olduğunu görür.
 */
export function useDriverLocationSharing(driverId: string | null, enabled: boolean) {
  const [position, setPosition] = useState<DriverPosition | null>(null);
  const [lastSentAt, setLastSentAt] = useState<Date | null>(null);
  const [error, setError] = useState<LocationSharingError | null>(null);
  const lastSentRef = useRef(0);
  const latestRef = useRef<DriverPosition | null>(null);

  useEffect(() => {
    if (!enabled || !driverId) return;
    if (!navigator.geolocation) {
      setError("unsupported");
      return;
    }

    const send = (p: DriverPosition) => {
      lastSentRef.current = Date.now();
      updateDriverLocation(driverId, p.lat, p.lng)
        .then(() => {
          setLastSentAt(new Date());
          setError((prev) => (prev === "send" ? null : prev));
        })
        .catch(() => setError("send"));
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        latestRef.current = p;
        setPosition(p);
        setError((prev) => (prev === "send" ? prev : null));
        if (Date.now() - lastSentRef.current >= SEND_INTERVAL_MS) send(p);
      },
      (err) => setError(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );

    const heartbeat = window.setInterval(() => {
      if (latestRef.current && Date.now() - lastSentRef.current >= SEND_INTERVAL_MS) {
        send(latestRef.current);
      }
    }, SEND_INTERVAL_MS);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      window.clearInterval(heartbeat);
    };
  }, [driverId, enabled]);

  return { position, lastSentAt, error };
}
